import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Save, Plus, X, ExternalLink } from "lucide-react";
import api, { formatErr, fileUrl, getToken } from "../lib/apiClient";

const TERMS_OPTIONS = ["C.O.D.", "Due on Receipt", "Net 15", "Net 30", "Net 60", "Custom"];

export default function Settings() {
  const [data, setData] = useState({ business_name: "", address: "", phone: "", email: "", hst_number: "", hst_rate: 13, hst_rates: [], default_payment_terms: "C.O.D.", portal_company_name: "", portal_tagline: "", portal_primary_color: "", portal_accent_color: "", portal_support_email: "", portal_support_phone: "", portal_support_address: "", invoice_footer_message: "", invoice_terms_url: "", invoice_terms_text: "Terms of Service", invoice_terms_long: "", reminder_schedule: { interval_days: 7, start_from: "due_date", max_reminders: 20, escalating_tone: false } });
  const [hasLogo, setHasLogo] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [logoVersion, setLogoVersion] = useState(0);

  const load = async () => {
    const { data: s } = await api.get("/settings");
    setData({
      ...s,
      hst_rates: s.hst_rates || [],
      reminder_schedule: s.reminder_schedule || { interval_days: 7, start_from: "due_date", max_reminders: 20, escalating_tone: false },
    });
    setHasLogo(!!s.logo_path);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    try {
      const payload = {
        ...data,
        hst_rate: Number(data.hst_rate),
        hst_rates: (data.hst_rates || []).filter((r) => r.label && r.label.trim()).map((r) => ({
          label: r.label.trim(),
          rate: Number(r.rate) || 0,
        })),
      };
      await api.put("/settings", payload);
      setMsg("Settings saved");
      setTimeout(() => setMsg(""), 3000);
    } catch (e) { setErr(formatErr(e)); }
  };

  const addHstRate = () => setData({ ...data, hst_rates: [...(data.hst_rates || []), { label: "", rate: 0 }] });
  const updateHstRate = (i, patch) => setData({
    ...data,
    hst_rates: (data.hst_rates || []).map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
  });
  const removeHstRate = (i) => setData({
    ...data,
    hst_rates: (data.hst_rates || []).filter((_, idx) => idx !== i),
  });

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHasLogo(true);
      setLogoVersion((v) => v + 1);
    } catch (e) { setErr(formatErr(e)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div data-testid="settings-page" className="max-w-3xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Company profile and tax preferences.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="settings-quick-links">
        <Link
          to="/settings/company"
          data-testid="settings-nav-company"
          className="px-4 py-2 border border-[#0F4C81] text-[#0F4C81] text-sm rounded-md hover:bg-[#0F4C81]/5 flex items-center gap-2 font-medium"
        >
          Company Info <ExternalLink size={13} />
        </Link>
        <Link
          to="/settings/notifications"
          data-testid="settings-nav-notifications"
          className="px-4 py-2 border border-[#0F4C81] text-[#0F4C81] text-sm rounded-md hover:bg-[#0F4C81]/5 flex items-center gap-2 font-medium"
        >
          Notifications <ExternalLink size={13} />
        </Link>
        <Link
          to="/settings/estimates"
          data-testid="settings-nav-estimates"
          className="px-4 py-2 border border-[#0F4C81] text-[#0F4C81] text-sm rounded-md hover:bg-[#0F4C81]/5 flex items-center gap-2 font-medium"
        >
          Estimates <ExternalLink size={13} />
        </Link>
        <Link
          to="/settings/jobs"
          data-testid="settings-nav-jobs"
          className="px-4 py-2 border border-[#0F4C81] text-[#0F4C81] text-sm rounded-md hover:bg-[#0F4C81]/5 flex items-center gap-2 font-medium"
        >
          Jobs <ExternalLink size={13} />
        </Link>
        <Link
          to="/settings/invoices"
          data-testid="settings-nav-invoices"
          className="px-4 py-2 border border-[#0F4C81] text-[#0F4C81] text-sm rounded-md hover:bg-[#0F4C81]/5 flex items-center gap-2 font-medium"
        >
          Invoices → Customer view <ExternalLink size={13} />
        </Link>
        <Link
          to="/settings/account"
          data-testid="settings-nav-account"
          className="px-4 py-2 border border-[#0F4C81] text-[#0F4C81] text-sm rounded-md hover:bg-[#0F4C81]/5 flex items-center gap-2 font-medium"
        >
          Account <ExternalLink size={13} />
        </Link>
        <Link
          to="/settings/api-keys"
          data-testid="settings-nav-api-keys"
          className="px-4 py-2 border border-[#0F4C81] text-[#0F4C81] text-sm rounded-md hover:bg-[#0F4C81]/5 flex items-center gap-2 font-medium"
        >
          API Keys <ExternalLink size={13} />
        </Link>
      </div>

      {msg && <div data-testid="settings-saved" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded">{msg}</div>}
      {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}

      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
          <div>
            <h3 className="font-display font-semibold">Company Logo</h3>
            <p className="text-xs text-slate-500 mt-1">Appears on invoices and estimates · PNG / JPG · max 5MB</p>
          </div>
          <div className="flex items-center gap-4">
            {hasLogo && (
              <img
                key={logoVersion}
                src={fileUrl(`/settings/logo`) + `&v=${logoVersion}`}
                alt="Logo"
                className="h-16 w-auto max-w-[120px] object-contain border border-slate-200 rounded p-1"
                data-testid="settings-logo-preview"
              />
            )}
            <label data-testid="upload-logo-btn" className="px-3 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 cursor-pointer flex items-center gap-2">
              <Upload size={14} /> {uploading ? "Uploading..." : hasLogo ? "Replace" : "Upload"}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
            </label>
          </div>
        </div>
      </div>

      <form onSubmit={save} className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <h3 className="font-display font-semibold border-b border-slate-100 pb-3 mb-2">Business Profile</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Business Name</label>
            <input data-testid="settings-business_name" value={data.business_name || ""} onChange={(e) => setData({ ...data, business_name: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
          </div>
          <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Phone</label>
            <input data-testid="settings-phone" value={data.phone || ""} onChange={(e) => setData({ ...data, phone: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
          </div>
          <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Email</label>
            <input data-testid="settings-email" type="email" value={data.email || ""} onChange={(e) => setData({ ...data, email: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
          </div>
          <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">HST Number</label>
            <input data-testid="settings-hst_number" value={data.hst_number || ""} onChange={(e) => setData({ ...data, hst_number: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
          </div>
          <div className="sm:col-span-2"><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Address</label>
            <textarea data-testid="settings-address" rows={5} value={data.address || ""} onChange={(e) => setData({ ...data, address: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
          </div>
          <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Default HST Rate (%)</label>
            <input data-testid="settings-hst_rate" type="number" step="0.01" value={data.hst_rate} onChange={(e) => setData({ ...data, hst_rate: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81] tabular" />
            <p className="text-xs text-slate-500 mt-1">Default 13% (Ontario)</p>
          </div>
          <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Default Payment Terms</label>
            <select data-testid="settings-default-terms" value={data.default_payment_terms || "C.O.D."} onChange={(e) => setData({ ...data, default_payment_terms: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]">
              {TERMS_OPTIONS.map((t) => <option key={t}>{t}</option>)}
            </select>
            <p className="text-xs text-slate-500 mt-1">Applied to every new invoice</p>
          </div>
          <div className="sm:col-span-2">
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs">
              <div className="font-medium text-slate-900 mb-1">Numbering: one record, one number</div>
              <div className="text-slate-600">
                Each job IS its own invoice — they share the same number for their entire lifecycle.
                E.g. <code className="text-slate-700">00042</code> is both the job number and the invoice number.
                The "Open Jobs" sidebar shows unpaid records; the "Invoices" sidebar shows every record (paid or not).
              </div>
            </div>
          </div>
        </div>
        <div className="pt-2">
          <button type="submit" data-testid="save-settings-btn" className="px-5 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Save size={14} /> Save Settings</button>
        </div>
      </form>

      <form onSubmit={save} className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <div className="border-b border-slate-100 pb-3 mb-2 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold">Alternate Tax Rates</h3>
            <p className="text-xs text-slate-500 mt-1">Add additional HST/GST/Zero-rated tax presets. Switch between them per invoice via the HST dropdown.</p>
          </div>
          <button type="button" onClick={addHstRate} data-testid="add-hst-rate-btn" className="px-3 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50 flex items-center gap-1.5 font-medium"><Plus size={12}/> Add rate</button>
        </div>
        {(data.hst_rates || []).length === 0 ? (
          <div className="text-sm text-slate-500 italic text-center py-4">No alternate rates yet. The default {data.hst_rate}% is always available.</div>
        ) : (
          <div className="space-y-2" data-testid="hst-rates-list">
            {(data.hst_rates || []).map((r, i) => (
              <div key={i} data-testid={`hst-rate-row-${i}`} className="grid grid-cols-[1fr_140px_40px] gap-2 items-end">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Label</label>
                  <input data-testid={`hst-rate-label-${i}`} value={r.label} onChange={(e) => updateHstRate(i, { label: e.target.value })} placeholder="e.g. GST only, Zero-rated" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Rate (%)</label>
                  <input data-testid={`hst-rate-value-${i}`} type="number" step="0.01" value={r.rate} onChange={(e) => updateHstRate(i, { rate: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81] tabular" />
                </div>
                <button type="button" onClick={() => removeHstRate(i)} data-testid={`remove-hst-rate-${i}`} className="p-2 text-slate-400 hover:text-red-600 border border-slate-300 rounded mb-0.5"><X size={14}/></button>
              </div>
            ))}
          </div>
        )}
        <div className="pt-2">
          <button type="submit" data-testid="save-hst-rates-btn" className="px-5 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Save size={14}/> Save Tax Rates</button>
        </div>
      </form>

      <form onSubmit={save} className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <div className="border-b border-slate-100 pb-3 mb-2">
          <h3 className="font-display font-semibold">Customer Portal Branding</h3>
          <p className="text-xs text-slate-500 mt-1">
            Controls the look of the public portal at <code className="text-slate-700">/portal/login</code>. Leave blank to inherit your business profile.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Portal Display Name</label>
            <input data-testid="settings-portal_company_name" value={data.portal_company_name || ""} onChange={(e) => setData({ ...data, portal_company_name: e.target.value })} placeholder={data.business_name || "Your Business"} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
          </div>
          <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Tagline</label>
            <input data-testid="settings-portal_tagline" value={data.portal_tagline || ""} onChange={(e) => setData({ ...data, portal_tagline: e.target.value })} placeholder="View estimates and invoices" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
          </div>
          <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Primary Color</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="settings-portal_primary_color" type="color" value={data.portal_primary_color || "#0F4C81"} onChange={(e) => setData({ ...data, portal_primary_color: e.target.value })} className="w-12 h-10 border border-slate-300 rounded-md p-1" />
              <input value={data.portal_primary_color || ""} onChange={(e) => setData({ ...data, portal_primary_color: e.target.value })} placeholder="#0F4C81" className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
            </div>
          </div>
          <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Accent Color</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="settings-portal_accent_color" type="color" value={data.portal_accent_color || "#0EA5E9"} onChange={(e) => setData({ ...data, portal_accent_color: e.target.value })} className="w-12 h-10 border border-slate-300 rounded-md p-1" />
              <input value={data.portal_accent_color || ""} onChange={(e) => setData({ ...data, portal_accent_color: e.target.value })} placeholder="#0EA5E9" className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
            </div>
          </div>
          <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Support Phone</label>
            <input data-testid="settings-portal_support_phone" value={data.portal_support_phone || ""} onChange={(e) => setData({ ...data, portal_support_phone: e.target.value })} placeholder={data.phone} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
          </div>
          <div><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Support Email</label>
            <input data-testid="settings-portal_support_email" type="email" value={data.portal_support_email || ""} onChange={(e) => setData({ ...data, portal_support_email: e.target.value })} placeholder={data.email} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
          </div>
          <div className="sm:col-span-2"><label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Support Address</label>
            <input data-testid="settings-portal_support_address" value={data.portal_support_address || ""} onChange={(e) => setData({ ...data, portal_support_address: e.target.value })} placeholder={data.address} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
          </div>
        </div>
        <div className="pt-2 flex items-center gap-3">
          <button type="submit" data-testid="save-portal-branding-btn" className="px-5 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Save size={14} /> Save Branding</button>
          <a href="/portal/login" target="_blank" rel="noopener noreferrer" data-testid="preview-portal-link" className="text-sm text-[#0F4C81] hover:underline">Preview portal →</a>
        </div>
      </form>

      <form onSubmit={save} className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <div className="border-b border-slate-100 pb-3 mb-2">
          <h3 className="font-display font-semibold">Invoice Email Footer & Terms of Service</h3>
          <p className="text-xs text-slate-500 mt-1">
            Footer appears on every invoice email after the line items. Paste your full Terms of Service text below — customers click a link in their invoice email to read it on a one-page web view (no portal sign-in needed).
          </p>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Permanent message</label>
            <textarea
              data-testid="settings-invoice_footer_message"
              value={data.invoice_footer_message || ""}
              onChange={(e) => setData({ ...data, invoice_footer_message: e.target.value })}
              rows={5}
              placeholder="Thank you for your business! Payments by e-Transfer to billing@yourbusiness.ca…"
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81] resize-y"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Terms of Service text</label>
              {data.invoice_terms_long && (
                <a href="/api/public/terms" target="_blank" rel="noopener noreferrer" data-testid="preview-terms-link" className="text-xs text-[#0F4C81] hover:underline flex items-center gap-1">
                  Preview <ExternalLink size={11}/>
                </a>
              )}
            </div>
            <textarea
              data-testid="settings-invoice_terms_long"
              value={data.invoice_terms_long || ""}
              onChange={(e) => setData({ ...data, invoice_terms_long: e.target.value })}
              rows={10}
              placeholder="1. Payment Terms&#10;All invoices are due upon receipt unless otherwise stated…&#10;&#10;2. Limitation of Liability&#10;…&#10;&#10;Use a blank line between sections."
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81] resize-y font-mono"
            />
            <p className="text-[11px] text-slate-500 mt-1">Customers will see a one-tap link in every invoice email: <code className="text-[#0F4C81]">/api/public/terms</code>. Leave blank to use the External URL below.</p>
          </div>

          <div className="grid sm:grid-cols-[1fr_220px] gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">External Terms URL (optional)</label>
              <input
                data-testid="settings-invoice_terms_url"
                type="url"
                value={data.invoice_terms_url || ""}
                onChange={(e) => setData({ ...data, invoice_terms_url: e.target.value })}
                placeholder="https://yourbusiness.ca/terms"
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
              />
              <p className="text-[11px] text-slate-500 mt-1">Used only when Terms of Service text above is blank.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Link text</label>
              <input
                data-testid="settings-invoice_terms_text"
                value={data.invoice_terms_text || ""}
                onChange={(e) => setData({ ...data, invoice_terms_text: e.target.value })}
                placeholder="Terms of Service"
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
              />
            </div>
          </div>
          <p className="text-[11px] text-slate-500">
            Customers will see: "By paying this invoice, you agree to our <span className="text-[#0F4C81] underline">{data.invoice_terms_text || "Terms of Service"}</span>."
          </p>
        </div>
        <div className="pt-2">
          <button type="submit" data-testid="save-invoice-footer-btn" className="px-5 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Save size={14} /> Save Invoice Footer</button>
        </div>
      </form>

      {/* Reminder schedule */}
      <form onSubmit={save} className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <div className="border-b border-slate-100 pb-3 mb-2">
          <h3 className="font-display font-semibold">Invoice Reminder Schedule</h3>
          <p className="text-xs text-slate-500 mt-1">
            How often to remind customers about unpaid invoices. Each invoice can override these defaults.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Send reminder every</label>
            <select
              data-testid="settings-reminder-interval"
              value={data.reminder_schedule?.interval_days || 7}
              onChange={(e) => setData({ ...data, reminder_schedule: { ...data.reminder_schedule, interval_days: parseInt(e.target.value) } })}
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
            >
              <option value={1}>1 day</option>
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Start counting from</label>
            <select
              data-testid="settings-reminder-start-from"
              value={data.reminder_schedule?.start_from || "due_date"}
              onChange={(e) => setData({ ...data, reminder_schedule: { ...data.reminder_schedule, start_from: e.target.value } })}
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
            >
              <option value="due_date">Due date (default)</option>
              <option value="sent_date">Sent date</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Max total reminders</label>
            <input
              type="number" min={1} max={50}
              data-testid="settings-reminder-max"
              value={data.reminder_schedule?.max_reminders || 20}
              onChange={(e) => setData({ ...data, reminder_schedule: { ...data.reminder_schedule, max_reminders: parseInt(e.target.value) || 20 } })}
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81] tabular"
            />
            <p className="text-[11px] text-slate-500 mt-1">Hard cap per invoice (default 20)</p>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer pt-2 text-sm text-slate-700">
          <input
            type="checkbox"
            data-testid="settings-reminder-escalating"
            checked={!!data.reminder_schedule?.escalating_tone}
            onChange={(e) => setData({ ...data, reminder_schedule: { ...data.reminder_schedule, escalating_tone: e.target.checked } })}
          />
          Escalate tone (Friendly → Firm → Final Notice) — <em className="text-slate-400 not-italic">coming soon, single template used today</em>
        </label>
        <div className="pt-2">
          <button type="submit" data-testid="save-reminders-btn" className="px-5 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Save size={14}/> Save Reminders</button>
        </div>
      </form>

      {/* Weekly Payday Email */}
      <form onSubmit={save} className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <div className="border-b border-slate-100 pb-3 mb-2">
          <h3 className="font-display font-semibold">💰 Weekly Payday Email</h3>
          <p className="text-xs text-slate-500 mt-1">
            Every Friday we'll email you the amount safely available to draw from the business after expenses, HST, and corp tax estimates.
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
          <input
            type="checkbox"
            data-testid="settings-payday-enabled"
            checked={!!data.weekly_payday_email_enabled}
            onChange={(e) => setData({ ...data, weekly_payday_email_enabled: e.target.checked })}
          />
          Send weekly payday email
        </label>
        <div className={data.weekly_payday_email_enabled ? "" : "opacity-50 pointer-events-none"}>
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Send to (owner email)</label>
          <input
            type="email"
            data-testid="settings-payday-email"
            value={data.weekly_payday_email_to || ""}
            onChange={(e) => setData({ ...data, weekly_payday_email_to: e.target.value })}
            placeholder={data.email || "owner@yourbusiness.ca"}
            className="mt-1 w-full sm:max-w-md px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]"
          />
          <p className="text-[11px] text-slate-500 mt-1">Defaults to the business email above if left blank.</p>
        </div>
        <div className="pt-2 flex flex-wrap gap-2">
          <button type="submit" data-testid="save-payday-btn" className="px-5 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Save size={14}/> Save</button>
          <button
            type="button"
            disabled={!data.weekly_payday_email_enabled}
            data-testid="send-payday-now-btn"
            onClick={async () => {
              try {
                // Save first, then trigger send
                await api.put("/settings", { ...data, hst_rate: Number(data.hst_rate) });
                await api.post("/my-money/send-payday-email-now");
                setMsg("Payday email sent");
                setTimeout(() => setMsg(""), 3000);
              } catch (e) { setErr(formatErr(e)); }
            }}
            className="px-5 py-2 border border-[#0F4C81] text-[#0F4C81] text-sm rounded-md font-medium hover:bg-blue-50 disabled:opacity-50"
          >
            Send test email now
          </button>
        </div>
      </form>
    </div>
  );
}
